import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { PaginatedSelect } from '@/components/paginated-select'
import { MultiCheckboxSelect } from '@/components/multi-checkbox-select'
import { get_users } from '@/api/user'
import {
  affectationService,
  AffectationProfile,
  CreateProfileInput,
  UpdateProfileInput,
} from '@/api/affectation'
import { useCaseTypes } from '@/hooks/use-case-types'
import { useToast } from '@/hooks/use-toast'

interface ProfileDialogProps {
  open: boolean
  profile?: AffectationProfile
  onClose: () => void
  onSaved: () => void
}

export function ProfileDialog({ open, profile, onClose, onSaved }: ProfileDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const isEdit = !!profile
  const { caseTypes } = useCaseTypes()

  const [selectedLawyer, setSelectedLawyer] = useState<{ id: string | number; name: string } | null>(null)
  const [selectedVilles, setSelectedVilles] = useState<number[]>([])
  const [selectedTribunalTypes, setSelectedTribunalTypes] = useState<number[]>([])
  const [selectedCaseTypes, setSelectedCaseTypes] = useState<number[]>([])
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open && profile) {
      setSelectedLawyer({ id: profile.user, name: profile.user_name })
      setSelectedVilles(profile.villes.map((v) => v.id))
      setSelectedTribunalTypes(profile.tribunal_types.map((tt) => tt.id))
      setSelectedCaseTypes(profile.case_types.map((ct) => ct.id))
      setIsActive(profile.is_active)
    } else if (open && !profile) {
      setSelectedLawyer(null)
      setSelectedVilles([])
      setSelectedTribunalTypes([])
      setSelectedCaseTypes([])
      setIsActive(true)
    }
  }, [open, profile])

  const { data: villes = [] } = useQuery({
    queryKey: ['affectation-villes'],
    queryFn: () => affectationService.listVilles(),
    enabled: open,
    staleTime: 10 * 60 * 1000,
  })

  const fetchTribunalTypes = (search: string) =>
    affectationService.searchTribunalTypes(search)

  const fetchLawyers = async (page: number, search?: string) => {
    const result = await get_users({
      page,
      page_size: 20,
      ordering: null,
      name: search || null,
      is_active: ['true'],
      roles: null,
    })
    return {
      items: result.results.map((u) => ({ id: u.id, name: u.name })),
      hasMore: !!result.next,
    }
  }

  const handleSave = async () => {
    if (!isEdit && !selectedLawyer) {
      toast({ title: t('aff_select_lawyer'), variant: 'destructive' })
      return
    }

    setSaving(true)
    try {
      if (isEdit) {
        const payload: UpdateProfileInput = {
          villes: selectedVilles,
          tribunal_types: selectedTribunalTypes,
          case_types: selectedCaseTypes,
          is_active: isActive,
        }
        await affectationService.updateProfile(profile!.id, payload)
        toast({ title: t('aff_profile_updated') })
      } else {
        const payload: CreateProfileInput = {
          user: String(selectedLawyer!.id),
          villes: selectedVilles,
          tribunal_types: selectedTribunalTypes,
          case_types: selectedCaseTypes,
          is_active: isActive,
        }
        await affectationService.createProfile(payload)
        toast({ title: t('aff_profile_created') })
      }
      onSaved()
      onClose()
    } catch (err: any) {
      const msg =
        err?.response?.data?.non_field_errors?.[0] ??
        err?.response?.data?.user?.[0] ??
        t('error')
      toast({ title: msg, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('aff_edit_profile') : t('aff_add_profile')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 overflow-y-auto max-h-[65vh] px-1">
          <div className="space-y-1">
            <Label className="text-xs">
              {t('aff_col_lawyer')} {!isEdit && '*'}
            </Label>
            {isEdit ? (
              <div className="h-9 px-3 flex items-center rounded-md border border-input bg-muted/50 text-sm text-muted-foreground">
                {profile?.user_name}
              </div>
            ) : (
              <PaginatedSelect
                placeholder={t('aff_select_lawyer')}
                fetchItems={fetchLawyers}
                value={selectedLawyer}
                onSelect={setSelectedLawyer}
                className="w-full"
              />
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t('aff_col_villes')}</Label>
            <MultiCheckboxSelect
              items={villes}
              selected={selectedVilles}
              onChange={setSelectedVilles}
              placeholder={t('aff_search_villes')}
              allLabel={t('aff_any')}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t('aff_col_tribunal_types')}</Label>
            <MultiCheckboxSelect
              fetchItems={fetchTribunalTypes}
              initialSelectedItems={profile?.tribunal_types}
              selected={selectedTribunalTypes}
              onChange={setSelectedTribunalTypes}
              placeholder={t('aff_search_tribunals')}
              allLabel={t('aff_any')}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t('aff_col_case_types')}</Label>
            <MultiCheckboxSelect
              items={caseTypes}
              selected={selectedCaseTypes}
              onChange={setSelectedCaseTypes}
              placeholder={t('aff_search_case_types')}
              allLabel={t('aff_any')}
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label className="text-sm">{isActive ? t('aff_active') : t('aff_inactive')}</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('loading') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
